<template>
  <div
    class="effect-cover__selection"
    :style="selectedEffectStyle"
    @mousedown="handleMouseDown"
    @dblclick="handleDblClick"
    @contextmenu.prevent="handleContextMenu"
  >
    <div
      class="effect-cover__hover effect-cover__hover--hidden"
      v-for="c in childStyles"
      :style="c.style"
      :key="c.id"
      :data-id="c.id"
    />
    <template v-for="vd in ['t', 'b']">
      <div
        v-for="hd in ['r', 'l']"
        :key="vd + hd"
        :class="['effect-cover__handle', 'effect-cover__handle--' + vd + hd]"
        :style="{ cursor: getResizeCursor(vd + hd) }"
        @mousedown="handleMouseDown($event, vd + hd)"
      ></div>
    </template>
    <el-tooltip
      placement="top"
      :content="selectedEffect.content.rotate + '\u00b0'"
      :disabled="!showTooltip"
      ref="tooltip"
      :visibleArrow="false"
    >
      <div class="effect-cover__rotate" @mousedown="handleRotateMouseDown" />
    </el-tooltip>

    <textarea
      class="effect-cover__input"
      v-if="showInput"
      v-model="selectedEffect.content.text"
      :style="editorStyle"
      @mousedown.stop
      @contextmenu.stop
      @input="handleInput"
      @blur="handleBlur"
      ref="textarea"
    />
  </div>
</template>
<script>
import { getEffectBoxStyle, rotateBy, isMac, betweenDelta } from '../../utils';
import { adjustSize } from '../../utils/detectLines';
import { matchFilter } from '../../utils/util';

export default {
  props: ['selectedEffect', 'clipping'],
  computed: {
    current() {
      return this.$store.state.currentProgress;
    },
    playing() {
      return this.$store.state.playing;
    },
    activeFragment() {
      return this.$store.getters.activeFragment;
    },
    allEffects() {
      return this.$store.state.effects;
    },
    scale() {
      return this.$store.state.playerScale / 100;
    },
    dragging: {
      get() {
        return this.$store.state.effectDragging;
      },
      set(value) {
        this.$store.commit('handleEffectDrag', value);
      }
    },
    showInput: {
      get() {
        return this.$store.state.effectEditing === this.selectedEffect.id;
      },
      set(value) {
        this.$store.commit('handleEffectEdit', value ? this.selectedEffect.id : null);
      }
    },
    selectedEffectStyle() {
      label: if (!this.playing && !this.clipping && this.selectedEffect) {
        if (this.activeFragment.background === this.selectedEffect.id) {
          break label;
        }
        if (!this.selectedEffect.locked) {
          const effectCopy = {
            ...this.selectedEffect
          };
          if (this.selectedEffect.container) {
            const efc = this.selectedEffect.content;
            const containerEFC = this.containerEffect.content;
            const containerCenter = { x: containerEFC.width / 2, y: containerEFC.height / 2 };
            const selfCenter = { x: efc.left + efc.width / 2, y: efc.top + efc.height / 2 };

            let rotate = efc.rotate;

            switch (containerEFC.flip) {
              case 'horizontal':
                selfCenter.x = containerEFC.width - selfCenter.x;
                rotate = -rotate;
                break;
              case 'vertical':
                selfCenter.y = containerEFC.height - selfCenter.y;
                rotate = -rotate;
                break;
            }

            const { x, y } = rotateBy(selfCenter, containerCenter, containerEFC.rotate);
            effectCopy.content = {
              ...efc,
              top: y + containerEFC.top - efc.height / 2,
              left: x + containerEFC.left - efc.width / 2,
              rotate: rotate + containerEFC.rotate
            };
          }
          let ret = getEffectBoxStyle(effectCopy);

          ['left', 'top', 'width', 'height'].forEach(key => {
            ret[key] = parseFloat(ret[key]) * this.scale + 'px';
          });
          return ret;
        }
      }
      return {
        display: 'none'
      };
    },
    childStyles() {
      if (this.selectedEffect && this.selectedEffect.type === 'group') {
        const gefc = this.selectedEffect.content;
        return this.selectedEffect.children.map(c => {
          const ef = this.allEffects[c];
          const efc = ef.content;
          let flipTop = efc.top;
          let flipLeft = efc.left;
          let flipRotate = efc.rotate;

          switch (gefc.flip) {
            case 'horizontal':
              flipLeft = gefc.width - efc.left - efc.width;
              flipRotate = -efc.rotate;
              break;
            case 'vertical':
              flipTop = gefc.height - efc.top - efc.height;
              flipRotate = -efc.rotate;
              break;
          }
          const ret = getEffectBoxStyle({
            ...ef,
            content: {
              ...efc,
              left: flipLeft,
              top: flipTop,
              rotate: flipRotate
            }
          });
          ['left', 'top', 'width', 'height'].forEach(key => {
            ret[key] = parseFloat(ret[key]) * this.scale + 'px';
          });

          return {
            id: c,
            style: ef.locked ? { display: 'none' } : ret
          };
        });
      }
      return [];
    },
    canAdjust() {
      return this.selectedEffect && this.selectedEffect.type === 'text'
    },
    editorStyle() {
      if (this.canAdjust) {
        const ret = {};
        const efc = this.selectedEffect.content;
        const aspectRatio = this.$store.getters.aspectRatio;
        const fontSize = efc.fontSize / aspectRatio;
        ret.fontSize = fontSize + 'px';
        ret.lineHeight = efc.lineHeight;
        ret.textAlign = efc.align;
        if (fontSize < 12) {
          ret.fontSize = '12px';
          ret.transform = `translate(-50%,-50%) scale(${fontSize / 12 * this.scale}) translate(50%,50%)`;
          ret.width = 1200 / this.scale / fontSize + '%';
          ret.height = 1200 / this.scale / fontSize + '%';
        } else {
          ret.transform = `translate(-50%,-50%) scale(${this.scale}) translate(50%,50%)`;
          ret.width = 100 / this.scale + '%';
          ret.height = 100 / this.scale + '%';
        }

        if (efc.shadow) {
          ret.textShadow = `${efc.shadowX}px ${efc.shadowY}px ${efc.shadowBlur}px ${efc.shadowColor}`;
        }

        ret.color = efc.color;
        ret.fontFamily = efc.fontFamily + ",SourceHanSansCN-Regular,sans-serif";
        ret.letterSpacing = (efc.letterSpacing / 100 || 0) + 'em';

        return ret;
      }

      if (this.selectedEffect && (this.selectedEffect.type === 'textContainer' || this.selectedEffect.type === 'wordArt')) {
        const ret = {};
        const efc = this.selectedEffect.content;
        const aspectRatio = this.$store.getters.aspectRatio;
        const fontSize = efc.fontSize / aspectRatio;
        ret.fontSize = fontSize + 'px';
        ret.lineHeight = efc.lineHeight;
        ret.textAlign = efc.align;
        if (fontSize < 12) {
          ret.fontSize = '12px';
          ret.transform = `translate(-50%,-50%) scale(${fontSize / 12 * this.scale}) translate(50%,50%)`;
          ret.width = 1200 / this.scale / fontSize + '%';
          ret.height = 1200 / this.scale / fontSize + '%';
        } else {
          ret.transform = `translate(-50%,-50%) scale(${this.scale}) translate(50%,50%)`;
          ret.width = 100 / this.scale + '%';
          ret.height = 100 / this.scale + '%';
        }

        ret.fontFamily = efc.fontFamily + ",SourceHanSansCN-Regular,sans-serif";
        ret.letterSpacing = (efc.letterSpacing / 100 || 0) + 'em';
        return ret;
      }
      return {};
    },
    adjustFontSizeMarker() {
      const effect = this.selectedEffect;
      if (effect && (effect.type === 'text' || effect.type === 'textContainer' || effect.type === 'wordArt')) {
        const efc = effect.content;
        if (efc.autoFontSize) {
          return [
            efc.fontFamily,
            efc.lineHeight,
            efc.width,
            efc.height,
            efc.letterSpacing,
            this.$store.state.fontStates[efc.fontFamily]
          ].join(',');
        }
      }
      return '';
    },
    containerEffect() {
      return (this.selectedEffect
        && this.selectedEffect.container
        && this.$store.state.effects[this.selectedEffect.container]
      ) || null;
    },
    showTooltip() {
      return this.dragging &&
        this.dragging.effectId === this.selectedEffect.id &&
        this.dragging.rotate;
    },
    displayRotation() {
      return this.showTooltip && this.selectedEffect.content.rotate;
    }
  },
  methods: {
    getResizeCursor(dir) {
      if (this.selectedEffect && this.selectedEffect.content) {
        let rotation = this.selectedEffect.content.rotate;
        if (this.containerEffect) {
          rotation += this.containerEffect.content.rotate || 0;
        }
        switch (dir) {
          case 'tl':
          case 'br':
            rotation += 45;
            break;
          default:
            rotation += 135;
        }

        switch (Math.round((rotation + 180) % 180 / 45)) {
          case 0:
          case 4:
            return 'ew-resize';
          case 1:
            return 'nwse-resize';
          case 2:
            return 'ns-resize';
          case 3:
            return 'nesw-resize';
        }
      }
      return 'default';
    },
    handleMouseDown(e, dir) {
      let cid;
      if (!e.button && e.target.tagName.toLowerCase() !== 'input') {
        const bcr = this.$parent.$el.getBoundingClientRect();
        let nextEffectId = null;

        const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

        if (dir) {
          e.stopPropagation();
        } else {
          let afterCurrent = false;

          if (e.target.getAttribute('data-id')) {
            cid = e.target.getAttribute('data-id');
          }

          // TODO: pass it down
          this.activeFragment.effects.some(eid => {
            const ef = this.allEffects[eid];
            const efr = ef.content;

            const [top, left, width, height] = [efr.top, efr.left, efr.width, efr.height].map(v => v * this.scale);
            // TODO: rotation-aware hit test
            const center = {
              x: left + width / 2 + bcr.left,
              y: top + height / 2 + bcr.top
            };
            const unrotatedMouse = rotateBy({
              x: e.clientX,
              y: e.clientY
            }, center, -efr.rotate || 0);

            if (afterCurrent
              && betweenDelta(this.current, ef.start + this.activeFragment.start, ef.duration)
              && betweenDelta(unrotatedMouse.x, left + bcr.left, width)
              && betweenDelta(unrotatedMouse.y, top + bcr.top, height)
            ) {
              if (ef.locked || ef.hidden) {
                return false;
              }
              const { timelineFilter, shapeNames, videoSeriesTagMode } = this.$store.state;
              if (!matchFilter(ef, timelineFilter, shapeNames, videoSeriesTagMode)) {
                return false;
              }
              nextEffectId = ef.id;
              return true;
            }
            if (this.selectedEffect === ef) {
              afterCurrent = true;
            }
            return false;
          });
        }
        const obj = {
          clientX: e.clientX,
          clientY: e.clientY,
          dir,
          effectId: this.selectedEffect.id,
          ctrlKey,
          next: nextEffectId
        };
        if (cid) {
          obj.childId = cid;
        }
        this.dragging = obj;
      }
    },
    selectEffect(e, id) {
      this.$store.commit('handleEffectSelect', [id]);
    },
    handleRotateMouseDown(e) {
      if (!e.button) {
        e.stopPropagation();
        const bcr = this.$el.getBoundingClientRect();
        this.dragging = {
          clientX: e.clientX,
          clientY: e.clientY,
          effectId: this.selectedEffect.id,
          rotate: [
            bcr.left + bcr.width / 2,
            bcr.top + bcr.height / 2
          ]
        };
      }
    },
    handleDblClick() {
      const effect = this.selectedEffect;
      switch (effect.type) {
        case 'text':
        case 'textContainer':
        case 'wordArt':
          console.log('dblclick on text');
          this.showInput = true;
          break;
        case 'container':
          this.$store.commit('handleContainerEdit', effect.id);
          this.$store.commit('handleCurrentIcon', 'material');
          this.$store.commit('handleCurrentMenu', 'MaterialMenu');
          break;
      }
    },
    handleInput(e) {
      const effect = this.selectedEffect;
      if (effect.content.autoFontSize) {
        if (!this.adjustPending) {
          this.adjustPending = setTimeout(() => {
            this.adjustPending = false;
            this.adjustFontSize(effect);
          }, 1e3);
        }
      }
    },
    handleBlur() {
      const effect = this.selectedEffect;
      if (effect && this.adjustPending) {
        console.log('handling blur');
        clearTimeout(this.adjustPending);
        this.adjustPending = false;
        this.adjustFontSize(effect);
      }

      console.log('clearing measuring effect');

      setTimeout(() => {
        if (this.showInput) {
          console.log('stop text editing');
          this.showInput = false;
        }
      }, 50);
    },
    async adjustFontSize(effect) {
      const fontSize = await adjustSize(effect.content, this.$store.getters.aspectRatio, effect.type === 'text' || effect.type === 'textContainer');
      effect.content.fontSize = Math.max(fontSize, 1);
      // message.info({
      //   key: 'fontSizeMessage',
      //   content: '若不满意于字号自动调整结果，可以于右侧面板手动调整。'
      // });
    },
    handleContextMenu(e) {
      this.$store.dispatch('openEffectContextMenu', {
        x: e.clientX,
        y: e.clientY,
        effectId: this.$store.getters.selectedEffect ? this.selectedEffect.id : null,
        cover: true
      });
    }
  },
  watch: {
    adjustFontSizeMarker(val, prev) {
      console.log('excuting watcher: adjustFontSizeMarker');
      if (val) {
        if (this.$store.state.recordDisabled) {
          console.log('Cannot detect font size.')
          return;
        }
        const effect = this.selectedEffect;
        const fontFamily = val.split(',')[0], prevFontFamily = prev.split(',')[0];
        if (!this.adjustFontSizeLock) {
          this.adjustFontSizeLock = true;
          setTimeout(() => {
            console.log('timeout was met');
            this.adjustFontSizeLock = false;
            this.adjustFontSize(effect);
            if (prevFontFamily && fontFamily !== prevFontFamily) {
              setTimeout(() => {
                this.adjustFontSize(effect);
              }, 1e3);
            }
          }, 60);
        }
      }
    },
    dragging(val, prev) {
      if (!val && prev.dir) {
        // TODO:
        console.log('dragging was stopped');
        if (this.selectedEffect.type === 'text') {
          if (this.selectedEffect.content.autoFontSize) {
            this.adjustFontSize(this.selectedEffect);
          }
        } else if (this.selectedEffect.type === 'group') {
          this.selectedEffect.children.forEach(c => {
            const ef = this.allEffects[c];
            if (ef.type === 'text' && ef.content.autoFontSize) {
              this.adjustFontSize(ef);
            }
          });
        }
      }
    },
    showInput(val) {
      if (val) {
        requestAnimationFrame(() => {
          const el = this.$refs.textarea;
          el && el.focus();
        });
      }
    },
    displayRotation() {
      if (this.showTooltip) {
        this.$refs.tooltip.updatePopper();
        this.$refs.tooltip.showPopper = true;
      }
    }
  },
  beforeDestroy() {
    this.showInput = null;
  }
}
</script>